import type { LC, LiveElement } from '@use-gpu/live';
import type { ArcLengthMap, VectorLike, VectorLikes } from '@use-gpu/core';
import type { Ease, EaseType, Keyframe, Lerpable, LerpableRecord, Spline, Tracks, TrackTypes } from './types';

import { bezierEase, clamp, makeArcLengthMap, queryArcLengthMap } from '@use-gpu/core';
import { extend, mutate, fence, useCallback, useDouble, useMemo, useOne } from '@use-gpu/live';
import { useTimeContext } from '../providers/time-provider';
import { useAnimationFrame, useNoAnimationFrame } from '../providers/loop-provider';
import { getRenderFunc } from '../hooks/useRenderProp';

import { EaseTypes, cloneLerpable } from './interpolate';

import mapValues from 'lodash/mapValues.js';
import zipObject from 'lodash/zipObject.js';

const π = Math.PI;

export type AnimateProps<T extends Lerpable | LerpableRecord> = {
  tracks?: T extends LerpableRecord ? Tracks<T> : never,
  types?: T extends LerpableRecord ? TrackTypes<T> : never,

  keyframes?: T extends Lerpable ? Keyframe<T>[] : never,
  prop?: T extends Lerpable ? string : never,
  type?: T extends Lerpable ? EaseType : never,

  loop?: boolean,
  mirror?: boolean,
  repeat?: number,
  ease?: Ease,

  delay?: number,
  rest?: number,
  duration?: number,
  speed?: number,

  paused?: boolean,

  render?: (value: T) => LiveElement,
  children?: LiveElement | ((value: T) => LiveElement),
};

export const Animate: LC<AnimateProps<any>> = <T extends Lerpable | LerpableRecord>(props: AnimateProps<T>) => {
  const {
    loop = false,
    mirror = false,
    repeat = Infinity,
    ease = 'cosine',

    delay = 0,
    rest = 0,
    speed = 1,
    paused = false,
    duration = null,

    tracks,
    types,
    keyframes,
    prop = 'value',
    type = 'number',

    children,
  } = props;

  // Prepare keyframe script
  const script: Tracks<any> | null = useMemo(() => (
    tracks ??
    (keyframes ? {[prop]: keyframes} : null)
  ), [tracks, keyframes, prop]);
  if (!script) return null;

  // Construct interpolators once
  const easeTypes = useMemo(() =>
    mapValues(script, (keyframes, k) => {
      if (Array.isArray(keyframes[0])) throw new Error("Keyframe type must be an object");

      const t = (types?.[k] ?? type) as EaseType;
      const et = EaseTypes[t];
      if (!et) throw new Error(`Unknown ease value type '${t}' for '${k}'`);
      return et;
    })
  , [script, types, type]);

  const splines = useMemo(() =>
    mapValues(script, (keyframes, k) => {
      const et = easeTypes[k];

      const out = [];
      const n = keyframes.length;
      for (let i = 0; i < n - 1; ++i) {
        out.push(et.spline(
          keyframes[i].value,
          keyframes[i + 1].value,
          keyframes[i].knots,
        ));
      }
      return out;
    })
  , [script, easeTypes]);

  const arcMaps = useMemo(() =>
    mapValues(script, (keyframes, k) => {
      const et = easeTypes[k];

      if (!keyframes.some(k => k.knots)) return [];

      const v1 = cloneLerpable(keyframes[0].value);
      const v2 = cloneLerpable(keyframes[0].value);

      const out = [];
      const n = keyframes.length;
      for (let i = 0; i < n - 1; ++i) {
        if (keyframes[i].knots) {
          const spline = splines[k][i];

          const measure = (t1: number, t2: number) => {
            spline(v1, t1);
            spline(v2, t2);
            return et.measure(v1, v2);
          };

          const map = makeArcLengthMap(measure);
          out.push(map);
        }
        else {
          out.push(null);
        }
      }
      return out;
    })
  , [script, easeTypes, splines]);

  const startedRef = useOne(() => ({current: -1}), script);
  const timeRef = useOne(() => ({current: 0}), script);

  const length = useMemo(() => {
    if (duration != null) return duration;
    const tracks = Array.from(Object.values(script));
    return tracks.reduce((length, keyframes) => Math.max(length, keyframes[keyframes.length - 1].time), 0)
  }, [script, duration]);

  const render = getRenderFunc(props);

  // To avoid garbage collection, make a double-buffered value object with copies of all values
  const [swapValues] = useDouble(() => mapValues(script, keyframes => cloneLerpable(keyframes[0].value)), Object.keys(script));

  // If rendering JSX children, optimize this too
  const [swapElements] = useDouble(() => children && !render ? extend(children, swapValues()) : null, [children, swapValues]);

  // But scalars can't be passed by reference, so track them
  const scalars = zipObject(Object.keys(script).filter(k => typeof script[k][0].value === 'number'));

  const Run = useCallback(() => {
    const {elapsed, delta, start} = useTimeContext();

    useOne(() => {
      // Reset internal clock if external loop was interrupted
      if (start !== startedRef.current) {
        startedRef.current = start;
        timeRef.current = elapsed;
      }
      // Increment clock
      else if (!paused) {
        timeRef.current += delta * speed;
      }
    }, elapsed);

    const time = Math.max(0, timeRef.current / 1000 - delay);
    const [t, max] = getLoopedTime(time, length, rest, loop ? repeat : 0, mirror);

    const values = swapValues();
    for (const k in values) values[k] = evaluateKeyframe(values[k], script[k], splines[k], arcMaps[k], t, ease);

    // Run if not paused or not past end
    if (!paused && time < max) useAnimationFrame();
    else useNoAnimationFrame();

    if (render) return tracks ? render(values as T) : render(values[prop] as T);
    else if (typeof children === 'object') {
      const elements = swapElements();
      for (const k in scalars) scalars[k] = values[k];
      mutate(elements, scalars);
      return elements;
    }

    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [script, swapValues, swapElements, delay, rest, length, speed, loop, mirror, repeat, ease, paused, render, children]);

  // Fence so that only continuation runs repeatedly
  return fence(null, Run);
};

const evaluateKeyframe = <T extends number | VectorLike | VectorLikes>(
  target: T,
  keyframes: Keyframe<T>[],
  splines: Spline<T>[],
  arcMaps: (ArcLengthMap | null)[],
  time: number,
  ease: string,
): T => {
  const i = getActiveKeyframe(keyframes, time);
  const a = keyframes[i];
  const b = keyframes[i + 1];

  const sp = splines[i];
  const am = arcMaps[i];

  const {time: start, bezier} = a;
  const {time: end} = b;
  const dt = end - start;

  const frameEase = a.ease ?? ease;

  const timeFraction = clamp(dt ? (time - start) / dt : 0, 0, 1);

  let easedFraction = timeFraction;
  if (frameEase === 'bezier') {
    if (!bezier) throw new Error("Missing bezier knots for bezier ease");
    const [x1, y1, x2, y2] = bezier;
    easedFraction = bezierEase(timeFraction, x1, y1, x2, y2);
  }
  else if (frameEase === 'cosine') {
    easedFraction = .5 - Math.cos(timeFraction * π) * .5;
  }
  else if (frameEase === 'hold') {
    easedFraction = 0;
  }

  const splineFraction = easedFraction && am
    ? queryArcLengthMap(am, easedFraction * am.length)
    : easedFraction;

  return sp(target, splineFraction);
};

const getActiveKeyframe = <T extends number | VectorLike | VectorLikes>(keyframes: Keyframe<T>[], time: number) => {
  const n = keyframes.length;
  let i = 0;
  for (; i < n - 2; ++i) {
    if (time < keyframes[i + 1].time) break;
  }
  return i;
};

const getLoopedTime = (time: number, duration: number, rest: number, repeat: number, mirror: boolean) => {
  const max = (duration + rest) * repeat + duration;
  let t = Math.min(max, time);

  const dp = duration + rest;
  if (mirror) {
    t = time % (dp * 2);
    if (t < dp) t = Math.min(duration, t);
    else t = duration - Math.min(duration, t - dp);
  }
  else {
    t = Math.min(duration, time % dp);
  }

  return [t, max];
}
